export const CONTACT = {
  phone: '(252) 985-8235',
  phoneHref: 'tel:2529858235',
  email: 'customercare@trimmedliving.com',
  region: 'Central & Eastern North Carolina',
}

export const SERVICE_NAMES = [
  'Patios & Stone Walkways',
  'Retaining, Seating, & Garden Walls',
  'Pergolas, Decks & Arbors',
  '12V Low-Voltage Landscape Lighting',
  'Fences & Raised Garden Beds',
  'Full Landscape & Hardscape Design',
] as const

export interface Service {
  name: string
  icon: 'patio' | 'wall' | 'pergola' | 'light' | 'fence' | 'design'
  body: string
}

export const SERVICES: Service[] = [
  {
    name: SERVICE_NAMES[0],
    icon: 'patio',
    body: 'Natural stone and interlocking paver installations built on a solid, unyielding foundation.',
  },
  {
    name: SERVICE_NAMES[1],
    icon: 'wall',
    body: 'Heavy-duty block and stone walls engineered for structural retention and clean elevation transitions.',
  },
  {
    name: SERVICE_NAMES[2],
    icon: 'pergola',
    body: 'Custom exterior carpentry, covered shade pergolas, and outdoor living structures.',
  },
  {
    name: SERVICE_NAMES[3],
    icon: 'light',
    body: 'Solid brass path fixtures, architectural tree wash, and watertight connection hubs.',
  },
  {
    name: SERVICE_NAMES[4],
    icon: 'fence',
    body: 'Custom perimeter fencing, decorative borders, and tailored timber garden beds.',
  },
  {
    name: SERVICE_NAMES[5],
    icon: 'design',
    body: 'Turnkey outdoor transformations from conceptual layout to finished stonework and plantings.',
  },
]

export interface ProjectPhoto {
  src: string
  alt: string
  label: string
}

export interface Project {
  title: string
  caption: string
  location: string
  scope: string[]
  photos: ProjectPhoto[]
}

const DRIVE = (id: string) => `https://lh3.googleusercontent.com/d/${id}=w1400`

export const PROJECTS: Project[] = [
  {
    title: 'Complete Outdoor Living & Hardscape Build',
    caption:
      'Multi-feature installation combining dry-set pavers, retaining walls, and custom exterior carpentry.',
    location: 'Nash County, NC',
    scope: [
      'Dry-set paver patio and connecting walkways',
      'Segmental block retaining walls with drainage',
      'Custom cedar carpentry and outdoor living structure',
      'Solid brass path lighting tied to one transformer',
    ],
    photos: [
      { label: 'Site', src: DRIVE('1lQZ-ZEzIHdy27CQXRvVvNomrQpZn7dAH'), alt: 'Site at the start of the build, graded and marked out' },
      { label: 'Excavation', src: DRIVE('1nJSYf-jAhcH_hsf1b10l_BRZk4NWhX_B'), alt: 'Excavated and compacted base prepared for the paver field' },
      { label: 'Pavers', src: DRIVE('1kHm_HcqdKqbj_tCByY8SUAznVSw3H82y'), alt: 'Dry-set pavers being laid across the new patio area' },
      { label: 'Retaining', src: DRIVE('191mVA37PAnZuiaoAPqhYPFc1TQc0J08D'), alt: 'Stone retaining wall built to hold the elevation change' },
      { label: 'Carpentry', src: DRIVE('1IOeSGhbV2LH8ovSiw5qjgCHYyG0gYgXp'), alt: 'Custom exterior carpentry framing the outdoor living space' },
      { label: 'Wide', src: DRIVE('1cluMh-kVMbZ4N9mXJjqirq7srzpm1TcF'), alt: 'Wide view of the finished hardscape and structure together' },
      { label: 'Detail', src: DRIVE('14YN3LtN2rM3gdBmhRQZuZRSea43Sl8Bb'), alt: 'Close detail of the paver joints and stone edging' },
      { label: 'Final', src: DRIVE('1bD7LskazoK3sJSwoN8lu2WEHBaufR5p5'), alt: 'Completed build viewed from the edge of the patio' },
    ],
  },
  {
    title: 'Custom Cedar Shade Pergola & Outdoor Living',
    caption: 'Handcrafted exterior carpentry built for shade, airflow, and long-term durability.',
    location: 'Edgecombe County, NC',
    scope: [
      'Custom cedar pergola with slatted shade roof',
      'Exposed beam joinery and hidden fasteners',
      'Integrated seating and paver apron',
      'Low-voltage lighting for evening use',
    ],
    photos: [
      { label: 'Framing', src: DRIVE('1HTG9yTguh_W4hRh46KScvUzSjUfLdZ5d'), alt: 'Cedar posts and beams set out and framed for the pergola' },
      { label: 'Structure', src: DRIVE('1EhdXwgD5Hmv_Cr8O0o1Q44a_MNXSMzwe'), alt: 'Pergola frame standing with the top slats being fitted' },
      { label: 'Detail', src: DRIVE('1n5j88HjHX1OwZdaEOFRjwiSDrErCG_D_'), alt: 'Close view of the cedar joinery and hidden fasteners' },
      { label: 'Shade', src: DRIVE('1fPBLD0faIjofurhrkjxw0noOtEtctDLt'), alt: 'Finished shade structure casting a pattern across the patio' },
      { label: 'Living', src: DRIVE('1j7LXaqmyPb8SiZ4LBrAHshe8y4zwUgHv'), alt: 'Seating beneath the pergola in the completed outdoor space' },
      { label: 'Wide', src: DRIVE('112mWHD8t-w2PdwLk3QfsqxD9rAMJEksw'), alt: 'Wide view of the pergola and surrounding hardscape' },
    ],
  },
  {
    title: 'Engineered Stone Retaining Wall & Grade Control',
    caption:
      'Structural stone retaining walls engineered for slope stability, integrated drainage, and clean lines.',
    location: 'Wilson County, NC',
    scope: [
      'Engineered base course on compacted subgrade',
      'Block and stone retaining wall with batter',
      'Integrated drainage stone and outlet control',
      'Cap course and graded terrace behind the wall',
    ],
    photos: [
      { label: 'Grade', src: DRIVE('1oBiiBVZ8vvoq7XsnE71wE_MqJ_4F3Tzg'), alt: 'Sloped grade marked out before wall construction' },
      { label: 'Base', src: DRIVE('12nVs7cjM0CGQeyU_JnjEsgq_bT5qlPEu'), alt: 'Compacted base course and drainage stone set at the wall line' },
      { label: 'Block', src: DRIVE('1_C6wz7Qc4cPUV51QzxNsOvBmGqwSDtvr'), alt: 'Retaining block being laid and checked for level and batter' },
      { label: 'Cap', src: DRIVE('1NLi3vo9LrFShq_r5E56a_Lg-gDTMgU0f'), alt: 'Wall course finished with cap stones and drainage outlets' },
      { label: 'Wide', src: DRIVE('1lX_um9RwVCAuCtewPqguAN1PZuMZF4rp'), alt: 'Wide view of the completed wall holding the upper terrace' },
    ],
  },
  {
    title: '12V Low-Voltage Landscape Lighting',
    caption:
      'Custom brass and architectural low-voltage illumination designed for property security, pathway safety, and evening warmth.',
    location: 'Eastern North Carolina',
    scope: [
      'Solid brass path and step fixtures',
      'Architectural tree wash and facade uplighting',
      'Watertight connection hubs and one transformer',
      'Zoned control for security and evening warmth',
    ],
    photos: [
      { label: 'Path', src: DRIVE('1p9O5tykgp2Hwml8chsYKY6Wc0-Z-8lcJ'), alt: 'Brass path fixtures lighting a walkway after dark' },
      { label: 'Wash', src: DRIVE('15tKReHFhPcZxyMrhtZhk48j_lQcbO0DL'), alt: 'Architectural uplight washing a stone facade at night' },
      { label: 'Tree', src: DRIVE('1vBFILmhyFf-jLTtohYarzVTIH4ltUzt3'), alt: 'Tree canopy lit from below with warm low-voltage uplighting' },
      { label: 'Detail', src: DRIVE('1uVcU0bTrA3fbptXbR933GL_2tx7F0ViQ'), alt: 'Close view of a brass fixture and watertight connection hub' },
      { label: 'Beds', src: DRIVE('1aZCi33YhiFuhprvjLDWjG4q3yJp4JZU1'), alt: 'Planted beds glowing with soft accent lighting in the evening' },
      { label: 'Patio', src: DRIVE('1v9NOPTRpUJzu4PS396Mw3cZYl4LsUu1l'), alt: 'Patio and steps lit for safe evening use' },
      { label: 'Wide', src: DRIVE('1SVgU4WtySCIezgbS1I_23nOvavsUq-0i'), alt: 'Wide night view of the finished lighting scheme across the property' },
      { label: 'Entry', src: DRIVE('11jFKk8Yi9cKjDSeakFd1x4eSFk4--v4W'), alt: 'Entry planting and wall lit to guide visitors to the door' },
    ],
  },
]

export const HERO_IMAGE = 'https://static.lumi.new/c0/c029e123fc84d9507d4761dee2cdc5a2.webp'

export const LOGO_IMAGE = 'https://static.lumi.new/84/84e840ccc96fb0ca072ac48c4bbc58f8.webp'
